import React, { Component } from 'react';
import { truncate, humanize } from 'underscore.string';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import AddButton from '../../v2/buttons/AddButton';
import DeleteButton from '../../v2/buttons/DeleteButton';
import EditButton from '../../v2/buttons/EditButton';
import { CUSTOMER_TYPE } from '../../../constants/webhooks';

import {
  getWebhooks,
  createWebhook,
  deleteWebhook,
  updateWebhook,
} from '../../../utils/UserWebAPIUtils';
import ToastStore from '../../../stores/ToastStore';
import CargomaticCard from '../../v2/card';
import CargomaticModal from '../../v2/modal';
import ConfirmModal from '../../ui/modals/ConfirmModal';
import LoadingCircle from '../../ui/LoadingCircle';
import Form from './Form';
import Badge from '../../v2/Badge';
import SessionStore from '../../../stores/SessionStore';

class Webhooks extends Component {
  static propTypes = {
    accountType: PropTypes.string,
    userId: PropTypes.string.isRequired,
  };

  constructor(props) {
    super(props);
    this.state = {
      items: [],
      loaded: false,
      showConfirmModal: false,
      showFormModal: false,
      webhook: null,
    };
  }

  componentDidMount() {
    this.fetchWebhooks();
  }

  findWebhook(id) {
    return this.state.items.find((item) => item._id === id);
  }

  async fetchWebhooks() {
    const { t } = this.props;
    try {
      const result = await getWebhooks(this.props.userId);
      const {
        entity: { webhooks: items },
      } = result;

      const webhooksList = (items || []).map((item) => ({
        ...item,
        type: item.type || CUSTOMER_TYPE.value,
      }));
      let filteredList;
      if (!SessionStore.isAdmin()) {
        filteredList = webhooksList.filter(
          (item) => item.type === CUSTOMER_TYPE.value,
        );
      } else {
        filteredList = [...webhooksList];
      }
      this.setState({
        items: filteredList,
        loaded: true,
      });
    } catch (e) {
      ToastStore.addNotification({
        title: t('PROBLEM_LOADING_WEBHOOKS'),
        body: '',
        level: 'error',
      });
    }
  }

  handleAddWebhook = () => {
    const { userId } = this.props;

    this.setState({
      webhook: getNewWebhook(userId),
      showFormModal: true,
    });
  };

  handleEditWebhook = (id) => () => {
    const webhook = this.findWebhook(id);

    this.setState({
      webhook,
      showFormModal: true,
    });
  };

  handleDeleteWebhook = (id) => () => {
    const webhook = this.findWebhook(id);

    this.setState({
      showConfirmModal: true,
      webhook,
    });
  };

  handleConfirmDeleteWebhook = async () => {
    this.setState({
      showConfirmModal: false,
    });
    const { t } = this.props;

    try {
      const {
        webhook: { _id },
      } = this.state;
      await deleteWebhook(_id);

      ToastStore.addNotification({
        title: t('WEBHOOK_SUCCESSFULLY_DELETED'),
        body: '',
        level: 'success',
      });

      this.setState((prevState) => {
        const { items: currentItems, webhook } = prevState;

        const index = findIndex(webhook._id, currentItems);
        return {
          webhook: null,
          items: [
            ...currentItems.slice(0, index),
            ...currentItems.slice(index + 1),
          ],
        };
      });
    } catch (e) {
      ToastStore.addNotification({
        title: t('PROBLEM_DELETING_WEBHOOK'),
        body: '',
        level: 'error',
      });
    }
  };

  handleHideConfirmModal = () => {
    this.setState({
      showConfirmModal: false,
      webhook: null,
    });
  };

  handleHideWebhookForm = () => {
    this.setState({
      showFormModal: false,
      webhook: null,
    });
  };

  handleSaveForm = async (webhook) => {
    const { t } = this.props;
    try {
      let result;

      const isNewWebhook = !webhook._id;
      if (isNewWebhook) {
        result = await createWebhook(webhook);
      } else {
        result = await updateWebhook(webhook);
      }
      const {
        entity: { webhook: item },
      } = result;
      if (isNewWebhook) {
        this.setState((prevState) => {
          const { items: currentItems } = prevState;

          return {
            items: [...currentItems, item],
            webhook: null,
            showFormModal: false,
          };
        });
      } else {
        this.setState((prevState) => {
          const { items: currentItems } = prevState;

          const index = findIndex(webhook._id, currentItems);
          if (index === -1) {
            return;
          }

          return {
            webhook: null,
            showFormModal: false,
            items: [
              ...currentItems.slice(0, index),
              webhook,
              ...currentItems.slice(index + 1),
            ],
          };
        });
      }
      ToastStore.addNotification({
        title: t('WEBHOOK_SAVED_SUCCESSFULLY'),
        body: '',
        level: 'success',
      });
    } catch (e) {
      ToastStore.addNotification({
        title: t('PROBLEM_SAVING_WEBHOOK'),
        body: '',
        level: 'error',
      });
    }
  };

  renderForm() {
    if (!this.state.showFormModal) {
      return null;
    }

    return (
      <Form
        accountType={this.props.accountType}
        webhook={this.state.webhook}
        onCancel={this.handleHideWebhookForm}
        onSave={this.handleSaveForm}
      />
    );
  }

  renderItems() {
    const { loaded } = this.state;
    const { t } = this.props;
    if (!loaded) {
      return (
        <div className="p-6">
          <LoadingCircle text={t('LOADING_WEBHOOKS')} />
        </div>
      );
    }

    const { items } = this.state;

    if (items.length === 0) {
      return (
        <div className="px-6 pb-6">
          <p className="mt-0 mb-4">{t('NO_HOOKS')}</p>
          <button
            className="button button-green"
            onClick={this.handleAddWebhook}
          >
            <i className="fa fa-plus mr-1`" />
            <span className="inline-block">{t('CREATE_NEW_HOOK')}</span>
          </button>
        </div>
      );
    }

    return (
      <div className="overflow-x-auto">
        <table className="min-w-full default-table">
          <thead>
            <tr className="">
              <th className="">{t('URL')}</th>
              {SessionStore.isAdmin() && <th>{t('TYPE')}</th>}
              <th className="">{t('ERROR')} &#37;</th>
              <th className="">{t('ACTION', { count: 2 })}</th>
            </tr>
          </thead>
          <tbody className="bg-white">
            {items.map((item) => (
              <tr key={item._id} className="">
                <td style={{ width: '150px' }} className="" title={item.url}>
                  {truncate(item.url, 20)}
                </td>
                {SessionStore.isAdmin() && (
                  <td>
                    <Badge color={item.type === 'customer' ? 'green' : 'gray'}>
                      {humanize(item.type)}
                    </Badge>
                  </td>
                )}
                <td className="">
                  {item.totalCount > 0 && (
                    <div>
                      <span className="inline-block">
                        {calculatePercentage(item.errorCount, item.totalCount)}
                        &#37;
                      </span>
                      <span className="inline-block ml-2">
                        ({item.errorCount}{' '}
                        {t('ERROR', { count: item.errorCount })})
                      </span>
                    </div>
                  )}
                  {item.totalCount === 0 && <span>-</span>}
                </td>
                <td className="">
                  <div className="flex items-center -mx-2">
                    <div className="px-2">
                      <EditButton
                        title={t('EDIT_WEBHOOK')}
                        onClick={this.handleEditWebhook(item._id)}
                      />
                    </div>
                    <div className="px-2">
                      <DeleteButton
                        onClick={this.handleDeleteWebhook(item._id)}
                        title={t('DELETE_WEBHOOK')}
                      />
                    </div>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  render() {
    const { t } = this.props;
    return (
      <div>
        <CargomaticCard
          removeHorizontalPadding
          removeVerticalPadding
          header={() => (
            <div className="flex items-center justify-between">
              <h3 className="my-0 text-lg font-medium leading-6">
                {t('WEBHOOK_other', { count: 2 })}
              </h3>
              <AddButton onClick={this.handleAddWebhook} />
            </div>
          )}
        >
          {this.renderItems()}
        </CargomaticCard>
        {this.state.showFormModal && (
          <CargomaticModal
            show={this.state.showFormModal}
            onHide={this.handleHideWebhookForm}
            title={t('ADD_WEBHOOK')}
          >
            {this.renderForm()}
          </CargomaticModal>
        )}
        {this.state.showConfirmModal && (
          <ConfirmModal
            show={this.state.showConfirmModal}
            message={t('WEBHOOK_DELETION_CONFIRM')}
            title={t('CONFIRM_DELETION')}
            onConfirm={this.handleConfirmDeleteWebhook}
            onHide={this.handleHideConfirmModal}
          />
        )}
      </div>
    );
  }
}

function findIndex(targetId, items) {
  return items.findIndex((h) => h._id === targetId);
}

function getNewWebhook(account) {
  return {
    action: 'POST',
    url: 'https://',
    enabled: true,
    events: [],
    type: CUSTOMER_TYPE.value,
    account,
  };
}

function calculatePercentage(numerator, denominator) {
  return ((numerator / denominator) * 100).toFixed(0);
}

export default withTranslation()(Webhooks);
